/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.24.0
 */

import { ReactElement } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import isFunction from 'lodash/isFunction';

import { LinkBlock } from '@/main/components/common';
import { ROUTE_INQUIRY_ID, RoutePath } from '@/main/routes/constants/routesConstants';
import { stringFormatParams } from '@/main/utils/stringFormat';

import { IUserNotificationTranslation } from '../../internationalization/types';
import { GROUP_OVERFLOW_LIMIT, NOTIFICATIONS_FILTRED_INQUIRIES_LINK } from '../constants';
import { INotificationsEntity } from '../types';
import { getIsOverflowNotification } from './notificationsUtils';

export const getInquiriesNotificationData = (
    entity: INotificationsEntity,
    isGroup: boolean,
    translation: IUserNotificationTranslation,
    onLinkClick?: () => void,
): [string | ReactElement, ReactElement | undefined] => {
    const isOverflow = getIsOverflowNotification(entity);

    const content = getInquiriesNotificationContent(entity, translation, { isGroup, isOverflow, onLinkClick });

    const ItemLink = isGroup ? (
        <div>
            <LinkBlock
                withArrow
                url={isOverflow ? RoutePath.inquiries : NOTIFICATIONS_FILTRED_INQUIRIES_LINK}
                onClick={onLinkClick}
            >
                {translation.linkText.default}
            </LinkBlock>
        </div>
    ) : undefined;
    return [content, ItemLink];
};

interface IGetInquiriesNotificationContentOptions {
    isGroup: boolean;
    isOverflow: boolean;
    onLinkClick?: () => void;
}

export const getInquiriesNotificationContent = (
    entity: INotificationsEntity,
    translation: IUserNotificationTranslation,
    options: IGetInquiriesNotificationContentOptions,
) => {
    const { groupTitle, singleTitle, overflowTitle } = translation.content.inquiries;
    const { isGroup, isOverflow, onLinkClick } = options;
    if (!isGroup) {
        if (isFunction(onLinkClick)) {
            return (
                <Typography>
                    {singleTitle.preLink}
                    <LinkBlock
                        url={RoutePath.inquiryInfo.replace(ROUTE_INQUIRY_ID, String(entity.id))}
                        onClick={onLinkClick}
                    >
                        {entity.id}
                    </LinkBlock>
                    {singleTitle.afterLink}
                </Typography>
            );
        } else {
            return `${singleTitle.preLink}${entity.id}${singleTitle.afterLink}`;
        }
    }

    if (isOverflow) return stringFormatParams(overflowTitle, { count: GROUP_OVERFLOW_LIMIT });

    return stringFormatParams(groupTitle, {
        count: entity.count || '',
    });
};
